/**
 * `<RecordAbsencePanel/>` — *Record absence* from the roster toolbar (#2029).
 *
 * The founder (5 Oct 2026): "we need a way for the rosterer to manually
 * input who is taking emergency MC/leave on an already solved roster, as
 * right now it only assumes for people using the app". The two doors that
 * already did it — *Report off sick…* on a List line (or the grid's Edit
 * mode) and the Team page's leave form — were not found. This is one
 * visible door for both, beside *Solve*.
 *
 * **Why the lead comes:** someone has phoned in sick, or is suddenly on
 * leave, and most of the team has no account to say so themselves. **What
 * they see first:** a search box over the team, the kind (*Off sick (MC)*
 * or *Leave*) and today's date. **What they adjust straight away:** type the
 * name, pick the person, maybe add an end date, confirm.
 *
 * The write is the EXISTING lead on-behalf route both doors use
 * (`POST /teams/{id}/date-requests`, `source='lead'`): `request_type: "mc"`
 * — the row *Report off sick* writes, with its `mc_recover` gate and urgent
 * flag — or `"leave"`, the Team page's row. Several days are one row from
 * the first to the last day, as the route takes them (and as a member's own
 * multi-day MC lands), so there is one row to remove. The roster, the
 * ladder, Coverage and the Review cover inbox re-read on success
 * (`useCreateTeamDateRequest`), so *Review cover* appears when a seat is
 * short, exactly as after *Report off sick*. The success line names where
 * the lead removes it: the Team page's leave list.
 */
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useMemo, useState } from "react";
import { Link } from "react-router-dom";

import { apiFetch } from "../../api/client";
import { useCreateTeamDateRequest } from "../../api/useTeamDateRequests";
import { useTeamFeature } from "../../api/useTeamFeature";
import { useAuth } from "../../lib/AuthContext";
import { cn } from "../../lib/cn";
import { calendarTodayIso, formatShortDayLabel } from "../../lib/publishedDays";
import { sickLong, useSickClinical } from "../../lib/sickWording";
import type { RecordAbsenceRequest } from "../../store/recordAbsenceRequest";
import { Button } from "../ui";
import { reportErrorText } from "./ReportSickDialog";

import type { components } from "../../api/schema.gen";

type MemberRow = components["schemas"]["MemberRow"];

export interface RecordAbsencePanelProps {
  request: RecordAbsenceRequest;
  onClose: () => void;
}

type AbsenceKind = "mc" | "leave";

/** The sentence under the choices — the one *Report off sick* says. */
export const RECORD_ABSENCE_EFFECT =
  "This takes effect immediately and frees their seats for cover.";

/** `Wed 14 Oct` or `Wed 14 Oct – Fri 16 Oct`. */
function daysText(from: string, to: string): string {
  return from === to
    ? formatShortDayLabel(from)
    : `${formatShortDayLabel(from)} – ${formatShortDayLabel(to)}`;
}

/** Today when it is on screen, else the first date on screen. */
function defaultDay(dates: { from: string; to: string }): string {
  const today = calendarTodayIso();
  return today >= dates.from && today <= dates.to ? today : dates.from;
}

interface Recorded {
  name: string;
  kind: AbsenceKind;
  from: string;
  to: string;
}

export function RecordAbsencePanel({ request, onClose }: RecordAbsencePanelProps) {
  const { teamId, cycleId, dates } = request;
  const { accountId } = useAuth();
  const qc = useQueryClient();
  // Same read (and cache entry) as the Team page's leave form: every
  // member, with or without an account; the active ones are pickable.
  const membersQuery = useQuery({
    queryKey: ["team-members", teamId, accountId],
    enabled: Boolean(teamId && accountId),
    queryFn: () => apiFetch<MemberRow[]>(`/api/teams/${teamId}/members`),
  });
  const mcOn = useTeamFeature(teamId, "mc_recover");
  const sickClinical = useSickClinical(teamId);
  const create = useCreateTeamDateRequest(teamId);

  const [search, setSearch] = useState("");
  const [memberId, setMemberId] = useState<string | null>(null);
  // Null until the lead picks: MC unless the team has sick cover off.
  const [kindSet, setKindSet] = useState<AbsenceKind | null>(null);
  const [from, setFrom] = useState(() => defaultDay(dates));
  const [to, setTo] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [recorded, setRecorded] = useState<Recorded | null>(null);

  const kind: AbsenceKind = kindSet ?? (mcOn === false ? "leave" : "mc");
  const people = useMemo(
    () =>
      (membersQuery.data ?? [])
        .filter((m) => m.status === "active")
        .sort((a, b) => a.display_name_in_team.localeCompare(b.display_name_in_team)),
    [membersQuery.data],
  );
  const needle = search.trim().toLowerCase();
  const shown = needle
    ? people.filter((m) => m.display_name_in_team.toLowerCase().includes(needle))
    : people;
  const person = people.find((m) => m.id === memberId) ?? null;
  const last = to ?? from;
  const datesOk = Boolean(from) && last >= from;
  const canConfirm = person !== null && datesOk && !create.isPending;

  const confirm = () => {
    if (!person || !datesOk) return;
    setError(null);
    create.mutate(
      {
        membership_id: person.id,
        start_date: from,
        end_date: last,
        request_type: kind,
      },
      {
        onSuccess: () => {
          // The leave-review queue is keyed per window; the hook refreshes
          // the roster, the ladder, Coverage and the Review cover inbox.
          qc.invalidateQueries({ queryKey: ["leave-review", cycleId] });
          setRecorded({ name: person.display_name_in_team, kind, from, to: last });
        },
        onError: (err) => setError(reportErrorText(err)),
      },
    );
  };

  const startAgain = () => {
    setRecorded(null);
    setMemberId(null);
    setSearch("");
    setTo(null);
    setFrom(defaultDay(dates));
    create.reset();
  };

  if (recorded) {
    return (
      <div className="space-y-3 text-sm" data-testid="record-absence-done">
        <p className="text-sm text-fg" data-testid="record-absence-done-line">
          {recorded.name} is recorded {recorded.kind === "mc" ? "off sick" : "on leave"} on{" "}
          {daysText(recorded.from, recorded.to)}. To undo it, remove it on the{" "}
          <Link
            to={`/teams/${teamId}`}
            onClick={onClose}
            className="underline underline-offset-2"
            data-testid="record-absence-undo-link"
          >
            Home page
          </Link>{" "}
          under Leave &amp; call-blocks.
        </p>
        <div className="flex flex-wrap gap-2">
          <Button
            type="button"
            size="sm"
            variant="secondary"
            onClick={startAgain}
            data-testid="record-absence-another"
          >
            Record another
          </Button>
          <Button type="button" size="sm" variant="ghost" onClick={onClose}>
            Done
          </Button>
        </div>
      </div>
    );
  }

  const input = "rounded-sm border border-border bg-bg px-1.5 py-1 text-xs text-fg";

  return (
    <div className="space-y-4 text-sm" data-testid="record-absence-panel">
      <section className="space-y-1.5" aria-labelledby="record-absence-person">
        <h3 id="record-absence-person" className="text-xs font-semibold text-fg">
          Person
        </h3>
        <input
          type="search"
          aria-label="Search people"
          placeholder="Search by name"
          data-testid="record-absence-search"
          className={cn(input, "w-full")}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
        <ul
          className="max-h-48 space-y-0.5 overflow-y-auto rounded-sm border border-border p-1"
          data-testid="record-absence-people"
          aria-label="People"
        >
          {membersQuery.isLoading ? (
            <li className="px-1.5 py-1 text-xs text-fg-muted">Loading the team…</li>
          ) : shown.length === 0 ? (
            <li className="px-1.5 py-1 text-xs text-fg-muted">No one by that name.</li>
          ) : (
            shown.map((m) => (
              <li key={m.id}>
                <button
                  type="button"
                  aria-pressed={memberId === m.id}
                  data-testid={`record-absence-person-${m.id}`}
                  onClick={() => {
                    setError(null);
                    setMemberId(m.id);
                  }}
                  className={cn(
                    "w-full rounded-sm px-1.5 py-1 text-left text-xs",
                    memberId === m.id
                      ? "bg-accent-soft/50 text-fg"
                      : "text-fg-muted hover:bg-bg-subtle hover:text-fg",
                  )}
                >
                  {m.display_name_in_team}
                </button>
              </li>
            ))
          )}
        </ul>
      </section>

      <fieldset className="space-y-1.5">
        <legend className="text-xs font-semibold text-fg">Kind</legend>
        <div className="flex flex-wrap gap-3 text-xs">
          <label className="flex items-center gap-1.5">
            <input
              type="radio"
              name="record-absence-kind"
              value="mc"
              data-testid="record-absence-kind-mc"
              checked={kind === "mc"}
              disabled={mcOn === false}
              onChange={() => setKindSet("mc")}
            />
            {sickLong(sickClinical)}
          </label>
          <label className="flex items-center gap-1.5">
            <input
              type="radio"
              name="record-absence-kind"
              value="leave"
              data-testid="record-absence-kind-leave"
              checked={kind === "leave"}
              onChange={() => setKindSet("leave")}
            />
            Leave
          </label>
        </div>
        {mcOn === false ? (
          <p className="text-[11px] text-fg-muted" data-testid="record-absence-mc-off">
            Sick cover is off for this team. Turn it on in Setup to record{" "}
            {sickClinical ? "an MC" : "someone off sick"}.
          </p>
        ) : null}
      </fieldset>

      <section className="space-y-1.5" aria-labelledby="record-absence-dates">
        <h3 id="record-absence-dates" className="text-xs font-semibold text-fg">
          Dates
        </h3>
        <div className="flex flex-wrap items-center gap-2 text-xs">
          <label className="flex items-center gap-1.5">
            {to === null ? "Date" : "From"}
            <input
              type="date"
              data-testid="record-absence-from"
              className={input}
              value={from}
              onChange={(e) => setFrom(e.target.value)}
            />
          </label>
          {to !== null ? (
            <label className="flex items-center gap-1.5">
              To
              <input
                type="date"
                data-testid="record-absence-to"
                className={input}
                min={from || undefined}
                value={to}
                onChange={(e) => setTo(e.target.value)}
              />
            </label>
          ) : null}
        </div>
        <label className="flex items-center gap-1.5 text-[11px] text-fg">
          <input
            type="checkbox"
            data-testid="record-absence-several"
            checked={to !== null}
            onChange={(e) => setTo(e.target.checked ? from : null)}
          />
          More than one day
        </label>
      </section>

      <p className="text-xs text-fg-subtle" data-testid="record-absence-question">
        {person
          ? `Record ${person.display_name_in_team} ${kind === "mc" ? "off sick" : "on leave"} on ${
              datesOk ? daysText(from, last) : "these dates"
            }? `
          : ""}
        {RECORD_ABSENCE_EFFECT}
      </p>
      {error ? (
        <p className="text-xs text-danger" role="alert" data-testid="record-absence-error">
          {error}
        </p>
      ) : null}
      <div className="flex flex-wrap justify-end gap-2">
        <Button
          type="button"
          size="sm"
          variant="ghost"
          onClick={onClose}
          disabled={create.isPending}
        >
          Not now
        </Button>
        <Button
          type="button"
          size="sm"
          onClick={confirm}
          disabled={!canConfirm}
          data-testid="record-absence-confirm"
        >
          {create.isPending ? "Recording…" : kind === "mc" ? "Record off sick" : "Record leave"}
        </Button>
      </div>
    </div>
  );
}

export default RecordAbsencePanel;
